import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Label,
  TextInput,
  Textarea,
  Select,
  Alert,
  Spinner
} from 'flowbite-react';
import { HiPlus } from 'react-icons/hi';
import { leasApi, type LEACreateData } from '../../api/leas';
import { schoolsApi } from '../../api/schools';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';
import { SearchableMultiSelect, type MultiSelectOption } from '../common/SearchableMultiSelect';
import type { LEAListItem } from '../../types/api';

interface CreateLEAModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (leaId: number) => void;
}

interface FormData {
  // Organization Info
  nameOfInstitution: string;
  shortNameOfInstitution: string;
  leaTypeDescriptorId: number | '';
  leaCategoryDescriptorId: number | '';
  webSite: string;
  emailPattern: string;
  faxNumber: string;
  mainEmailAddress: string;
  boardMeetingSchedule: string;
  boardWebpage: string;
  districtsServed: number[];
  associatedOrganizations: number[];

  // Address & Phone
  streetAddress: string;
  city: string;
  postalCode: string;
  phone: string;
}

const initialFormData: FormData = {
  nameOfInstitution: '',
  shortNameOfInstitution: '',
  leaTypeDescriptorId: '',
  leaCategoryDescriptorId: '',
  webSite: '',
  emailPattern: '',
  faxNumber: '',
  mainEmailAddress: '',
  boardMeetingSchedule: '',
  boardWebpage: '',
  districtsServed: [],
  associatedOrganizations: [],
  streetAddress: '',
  city: '',
  postalCode: '',
  phone: '',
};

export const CreateLEAModal: React.FC<CreateLEAModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Descriptor options
  const [leaTypes, setLeaTypes] = useState<Descriptor[]>([]);
  const [leaCategories, setLeaCategories] = useState<Descriptor[]>([]);
  const [descriptorsLoading, setDescriptorsLoading] = useState(false);

  // Resolved descriptor IDs for auto-defaults
  const [operationalStatusActiveId, setOperationalStatusActiveId] = useState<number | null>(null);
  const [addressTypePhysicalId, setAddressTypePhysicalId] = useState<number | null>(null);
  const [phoneTypeMainOfficeId, setPhoneTypeMainOfficeId] = useState<number | null>(null);
  const [statePAId, setStatePAId] = useState<number | null>(null);
  const [countryUSId, setCountryUSId] = useState<number | null>(null);
  const [aiuParentOrgId, setAiuParentOrgId] = useState<number | null>(null);

  // Organization options for multi-select pickers
  const [orgOptions, setOrgOptions] = useState<MultiSelectOption[]>([]);          // LEAs only (for Districts Served)
  const [allOrgOptions, setAllOrgOptions] = useState<MultiSelectOption[]>([]);    // LEAs + Schools (for Associated Organizations)
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen]);

  // Load descriptors and AIU parent org on mount
  useEffect(() => {
    if (!isOpen) return;

    const loadDescriptors = async () => {
      setDescriptorsLoading(true);
      try {
        const [
          leaTypeList,
          leaCategoryList,
          opStatusList,
          addressTypeList,
          phoneTypeList,
          stateList,
          countryList,
        ] = await Promise.all([
          descriptorsApi.getByTypeName('LEA Type'),
          descriptorsApi.getByTypeName('LEA Category'),
          descriptorsApi.getByTypeName('Operational Status'),
          descriptorsApi.getByTypeName('Address Type'),
          descriptorsApi.getByTypeName('Phone Type'),
          descriptorsApi.getByTypeName('State'),
          descriptorsApi.getByTypeName('Country'),
        ]);

        setLeaTypes(leaTypeList);
        setLeaCategories(leaCategoryList);

        // Resolve auto-default descriptor IDs
        const activeStatus = opStatusList.find(d => d.codeValue === 'Active');
        if (activeStatus) setOperationalStatusActiveId(activeStatus.descriptorId);

        const physicalAddr = addressTypeList.find(d => d.codeValue === 'Physical');
        if (physicalAddr) setAddressTypePhysicalId(physicalAddr.descriptorId);

        const mainOffice = phoneTypeList.find(d => d.codeValue === 'MainOffice');
        if (mainOffice) setPhoneTypeMainOfficeId(mainOffice.descriptorId);

        const statePA = stateList.find(d => d.codeValue === 'PA');
        if (statePA) setStatePAId(statePA.descriptorId);

        const countryUS = countryList.find(d => d.codeValue === 'US');
        if (countryUS) setCountryUSId(countryUS.descriptorId);

        // Find AIU parent org and load all orgs for pickers
        const aiuName = import.meta.env.VITE_AIU_ORGANIZATION_NAME || 'Allegheny Intermediate Unit';
        try {
          setOrgOptionsLoading(true);
          const [orgs, schools] = await Promise.all([
            leasApi.list({ limit: 500 }),
            schoolsApi.list({ limit: 500 }),
          ]);
          const aiuOrg = orgs.find(o => o.nameOfInstitution === aiuName);
          if (aiuOrg) setAiuParentOrgId(aiuOrg.educationOrganizationId);

          // Build picker options from all LEAs with type indicator
          const leaOptions: MultiSelectOption[] = orgs.map((o: LEAListItem) => ({
            id: o.educationOrganizationId,
            label: o.leaType
              ? `${o.nameOfInstitution} [${o.leaType}]`
              : o.nameOfInstitution,
          }));
          setOrgOptions(leaOptions);

          // Build combined options (LEAs + Schools) for Associated Organizations picker
          const leaOrgIds = new Set(orgs.map(o => o.educationOrganizationId));
          const schoolOptions: MultiSelectOption[] = schools
            .filter(s => !leaOrgIds.has(s.educationOrganizationId))  // avoid duplicates
            .map(s => ({
              id: s.educationOrganizationId,
              label: `${s.nameOfInstitution} [School]`,
            }));
          setAllOrgOptions([...leaOptions, ...schoolOptions].sort((a, b) => a.label.localeCompare(b.label)));
        } catch {
          console.warn('Failed to load organizations for pickers');
        } finally {
          setOrgOptionsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load descriptors:', err);
        setError('Failed to load form options. Please try again.');
      } finally {
        setDescriptorsLoading(false);
      }
    };

    loadDescriptors();
  }, [isOpen]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user edits
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Check if selected LEA type is CTC or Jointure
  const selectedLeaType = leaTypes.find(
    t => t.descriptorId === Number(formData.leaTypeDescriptorId)
  );
  const showDistrictsServed =
    selectedLeaType?.codeValue === 'CTC' || selectedLeaType?.codeValue === 'Jointure';

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.nameOfInstitution.trim()) {
      errors.nameOfInstitution = 'Organization name is required';
    }
    if (!formData.leaTypeDescriptorId) {
      errors.leaTypeDescriptorId = 'LEA type is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Create the LEA
      const createData: LEACreateData = {
        nameOfInstitution: formData.nameOfInstitution.trim(),
        leaTypeDescriptorId: Number(formData.leaTypeDescriptorId),
        parentEducationOrganizationId: aiuParentOrgId || undefined,
        operationalStatusDescriptorId: operationalStatusActiveId || undefined,
        shortNameOfInstitution: formData.shortNameOfInstitution.trim() || undefined,
        webSite: formData.webSite.trim() || undefined,
        emailPattern: formData.emailPattern.trim() || undefined,
        faxNumber: formData.faxNumber.trim() || undefined,
        mainEmailAddress: formData.mainEmailAddress.trim() || undefined,
        leaCategoryDescriptorId: formData.leaCategoryDescriptorId
          ? Number(formData.leaCategoryDescriptorId)
          : undefined,
        boardMeetingSchedule: formData.boardMeetingSchedule.trim() || undefined,
        boardWebpage: formData.boardWebpage.trim() || undefined,
        districtsServed: showDistrictsServed && formData.districtsServed.length > 0
          ? formData.districtsServed
          : undefined,
        associatedOrganizations: showDistrictsServed && formData.associatedOrganizations.length > 0
          ? formData.associatedOrganizations
          : undefined,
      };

      const newLea = await leasApi.create(createData);

      // 2. Add address if any address fields provided
      const hasAddress =
        formData.streetAddress.trim() ||
        formData.city.trim() ||
        formData.postalCode.trim();

      if (hasAddress && addressTypePhysicalId) {
        try {
          await leasApi.addAddress(newLea.leaId, {
            streetNumberName: formData.streetAddress.trim() || undefined,
            city: formData.city.trim() || undefined,
            postalCode: formData.postalCode.trim() || undefined,
            stateDescriptorId: statePAId || undefined,
            countryDescriptorId: countryUSId || undefined,
            addressTypeDescriptorId: addressTypePhysicalId,
            isPrimary: true,
          });
        } catch (err) {
          console.error('Failed to add address (LEA was created):', err);
        }
      }

      // 3. Add phone if provided
      if (formData.phone.trim() && phoneTypeMainOfficeId) {
        try {
          await leasApi.addPhone(newLea.leaId, {
            phoneNumber: formData.phone.trim(),
            phoneTypeDescriptorId: phoneTypeMainOfficeId,
            primaryPhoneIndicator: true,
          });
        } catch (err) {
          console.error('Failed to add phone (LEA was created):', err);
        }
      }

      onSuccess(newLea.leaId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create organization'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible popup size="lg">
      <div className="p-6 flex flex-col max-h-[85vh]">
        <h3 className="text-xl font-medium text-gray-900 mb-6 flex-shrink-0">
          Add Organization
        </h3>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4 flex-shrink-0">
            {error}
          </Alert>
        )}

        {descriptorsLoading ? (
          <div className="flex justify-center items-center h-48">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="space-y-6 overflow-y-auto pr-2 flex-1 min-h-0">
            {/* Section: Organization Info */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Organization Info
              </h4>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <Label htmlFor="nameOfInstitution" className="mb-1">
                    Organization Name <span className="text-red-500">*</span>
                  </Label>
                  <TextInput
                    id="nameOfInstitution"
                    value={formData.nameOfInstitution}
                    onChange={(e) => updateField('nameOfInstitution', e.target.value)}
                    placeholder="e.g., Springfield School District"
                    color={fieldErrors.nameOfInstitution ? 'failure' : undefined}
                  />
                  {fieldErrors.nameOfInstitution && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.nameOfInstitution}</p>
                  )}
                </div>

                {/* Short Name */}
                <div>
                  <Label htmlFor="shortNameOfInstitution" className="mb-1">
                    Short Name
                  </Label>
                  <TextInput
                    id="shortNameOfInstitution"
                    value={formData.shortNameOfInstitution}
                    onChange={(e) => updateField('shortNameOfInstitution', e.target.value)}
                    placeholder="e.g., Springfield SD"
                  />
                </div>

                {/* LEA Type and Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="leaTypeDescriptorId" className="mb-1">
                      Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      id="leaTypeDescriptorId"
                      value={formData.leaTypeDescriptorId}
                      onChange={(e) => updateField('leaTypeDescriptorId', e.target.value ? Number(e.target.value) : '')}
                      color={fieldErrors.leaTypeDescriptorId ? 'failure' : undefined}
                    >
                      <option value="">Select type...</option>
                      {leaTypes.map(t => (
                        <option key={t.descriptorId} value={t.descriptorId}>
                          {t.shortDescription}
                        </option>
                      ))}
                    </Select>
                    {fieldErrors.leaTypeDescriptorId && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.leaTypeDescriptorId}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="leaCategoryDescriptorId" className="mb-1">
                      Category
                    </Label>
                    <Select
                      id="leaCategoryDescriptorId"
                      value={formData.leaCategoryDescriptorId}
                      onChange={(e) => updateField('leaCategoryDescriptorId', e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">Select category...</option>
                      {leaCategories.map(c => (
                        <option key={c.descriptorId} value={c.descriptorId}>
                          {c.shortDescription}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Website and Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="webSite" className="mb-1">Website</Label>
                    <TextInput
                      id="webSite"
                      value={formData.webSite}
                      onChange={(e) => updateField('webSite', e.target.value)}
                      placeholder="https://www.example.org"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mainEmailAddress" className="mb-1">Main Email</Label>
                    <TextInput
                      id="mainEmailAddress"
                      type="email"
                      value={formData.mainEmailAddress}
                      onChange={(e) => updateField('mainEmailAddress', e.target.value)}
                      placeholder="info@example.org"
                    />
                  </div>
                </div>

                {/* Email Pattern and Fax */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="emailPattern" className="mb-1">Email Pattern</Label>
                    <TextInput
                      id="emailPattern"
                      value={formData.emailPattern}
                      onChange={(e) => updateField('emailPattern', e.target.value)}
                      placeholder="FLast@example.org"
                    />
                  </div>
                  <div>
                    <Label htmlFor="faxNumber" className="mb-1">Fax</Label>
                    <TextInput
                      id="faxNumber"
                      value={formData.faxNumber}
                      onChange={(e) => updateField('faxNumber', e.target.value)}
                      placeholder="412-555-1234"
                    />
                  </div>
                </div>

                {/* Board Meeting Schedule */}
                <div>
                  <Label htmlFor="boardMeetingSchedule" className="mb-1">
                    Board Meeting Schedule
                  </Label>
                  <Textarea
                    id="boardMeetingSchedule"
                    value={formData.boardMeetingSchedule}
                    onChange={(e) => updateField('boardMeetingSchedule', e.target.value)}
                    placeholder="e.g., Second Monday of each month at 7:00 PM"
                    rows={2}
                  />
                </div>

                {/* Board Webpage */}
                <div>
                  <Label htmlFor="boardWebpage" className="mb-1">Board Webpage</Label>
                  <TextInput
                    id="boardWebpage"
                    value={formData.boardWebpage}
                    onChange={(e) => updateField('boardWebpage', e.target.value)}
                    placeholder="https://www.example.org/board"
                  />
                </div>

                {/* Districts Served & Associated Organizations - only for CTC/Jointure */}
                {showDistrictsServed && (
                  <>
                    <SearchableMultiSelect
                      label="Districts Served"
                      options={orgOptions}
                      selectedIds={formData.districtsServed}
                      onChange={(ids) => updateField('districtsServed', ids)}
                      placeholder="Search and select districts..."
                      loading={orgOptionsLoading}
                    />
                    <SearchableMultiSelect
                      label="Associated Organizations"
                      options={allOrgOptions}
                      selectedIds={formData.associatedOrganizations}
                      onChange={(ids) => updateField('associatedOrganizations', ids)}
                      placeholder="Search and select organizations or schools..."
                      loading={orgOptionsLoading}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Section: Address & Phone */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Address & Phone
              </h4>
              <div className="space-y-4">
                {/* Street Address */}
                <div>
                  <Label htmlFor="streetAddress" className="mb-1">Street Address</Label>
                  <TextInput
                    id="streetAddress"
                    value={formData.streetAddress}
                    onChange={(e) => updateField('streetAddress', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>

                {/* City and ZIP */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="mb-1">City</Label>
                    <TextInput
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Pittsburgh"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postalCode" className="mb-1">ZIP Code</Label>
                    <TextInput
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) => updateField('postalCode', e.target.value)}
                      placeholder="15213"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  State defaults to PA. You can change it after creation from the organization profile.
                </p>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="mb-1">Main Phone</Label>
                  <TextInput
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="412-555-1234"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-2 mt-6 pt-4 border-t flex-shrink-0">
          <Button color="gray" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || descriptorsLoading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              <>
                <HiPlus className="mr-2 h-4 w-4" />
                Create Organization
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
